import React from 'react';
import {
    Button, Row, Col, Spinner,
} from 'reactstrap';
import { formatTime12Hour } from '@s20/common/util/date';
import { getBitmaskLabels } from '@s20/common/util/number';
import { Can } from '../Can';
import { SmallButton } from '../common/forms/FormElements';
import CommonContext, { WeekdayAbbreviations } from '../Common';
import { EmployeeRestrictionType } from './EmployeeRestrictionForm';

const getRestrictionType = (info) => {
    if (info.employeeRestrictionTypeId === 3) return EmployeeRestrictionType.Time;
    if (info.restrictedEmployeeId) return EmployeeRestrictionType.Employee;
    return EmployeeRestrictionType.Customer;
};

const getNameCell = (info, restrictionType, currentEmployeeId) => {
    if (restrictionType === EmployeeRestrictionType.Time) {
        return getBitmaskLabels(info.restrictionDays, WeekdayAbbreviations).join(', ') || '—';
    }
    if (restrictionType === EmployeeRestrictionType.Employee) {
        return info.employeeId === currentEmployeeId
            ? info.restrictedEmployeeName
            : info.employeeName;
    }
    return info.companyName;
};

const getForemenCell = (info, restrictionType) => {
    if (restrictionType === EmployeeRestrictionType.Time) {
        return info.restrictionStartTime && info.restrictionEndTime
            ? `${formatTime12Hour(info.restrictionStartTime)} – ${formatTime12Hour(info.restrictionEndTime)}`
            : 'All day';
    }
    if (restrictionType === EmployeeRestrictionType.Employee) return '-';
    return info.foremenName;
};

const getBlockAllCell = (info, restrictionType) => {
    if (restrictionType === EmployeeRestrictionType.Time || restrictionType === EmployeeRestrictionType.Employee) return '-';
    return info.blockAll ? 'Yes' : 'No';
};

export class EmployeeRestrictionInfo extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            loading: true,
        };
    }

    componentDidMount() {
        this.populateState();
    }

    populateState() {
        this.setState({
            loading: false,
        });
    }

    render() {
        if (this.state.loading) {
            return (<Spinner />);
        }

        return (
            <>
                <Row className="pb-1">
                    <Col>
                        <div className="d-flex flex-row align-items-center justify-content-end">
                            {this.props.onViewHistory && (
                                <CommonContext.Consumer>
                                    {(value) => (
                                        <SmallButton
                                            disabled={!!value.formIsOpen}
                                            type="button"
                                            onClick={this.props.onViewHistory}
                                            className="mr-2"
                                        >
                                            <i className="fa fa-history fa-md mr-2" />
                                            View History
                                        </SmallButton>
                                    )}
                                </CommonContext.Consumer>
                            )}
                            <Can
                                I="create"
                                a="employee_restriction"
                            >
                                <CommonContext.Consumer>
                                    {(value) => (
                                        <SmallButton
                                            disabled={!!value.formIsOpen}
                                            type="button"
                                            onClick={this.props.onAddRestriction}
                                        >
                                            <i className="fa fa-plus-circle fa-md mr-2" />
                                            Add Restriction
                                        </SmallButton>
                                    )}
                                </CommonContext.Consumer>
                            </Can>
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <table className="table table-sm table-bordered">
                            <thead className="text-muted">
                                <tr>
                                    <th>Type</th>
                                    <th>Name</th>
                                    <th>Detail</th>
                                    <th>Block All</th>
                                    <Can
                                        I="edit"
                                        a="employee_restriction"
                                    >
                                        <th
                                            className="text-center"
                                            style={{ width: '30px' }}
                                        />
                                    </Can>
                                </tr>
                            </thead>
                            <tbody>
                                {!(this.props.restrictions ?? []).length ? (
                                    <tr>
                                        <td
                                            colSpan="5"
                                            className="text-center"
                                        >No restriction found.</td>
                                    </tr>
                                ) : this.props.restrictions.map((restrictionInfo) => {
                                    const restrictionType = getRestrictionType(restrictionInfo);
                                    const nameCell = getNameCell(restrictionInfo, restrictionType, this.props.employeeId);
                                    const foremenCell = getForemenCell(restrictionInfo, restrictionType);
                                    const blockAllCell = getBlockAllCell(restrictionInfo, restrictionType);

                                    return (
                                        <tr key={restrictionInfo.id}>
                                            <td className="text-capitalize">{restrictionType}</td>
                                            <td>{nameCell}</td>
                                            <td>{foremenCell}</td>
                                            <td>{blockAllCell}</td>
                                            <Can
                                                I="edit"
                                                a="employee_restriction"
                                            >
                                                <CommonContext.Consumer>
                                                    {(value) => (
                                                        <td>
                                                            <Button
                                                                disabled={!!value.formIsOpen}
                                                                type="button"
                                                                color="outline-primary"
                                                                onClick={() => this.props.onEditRestriction(restrictionInfo)}
                                                                size="sm"
                                                            >
                                                                Edit
                                                            </Button>
                                                        </td>
                                                    )}
                                                </CommonContext.Consumer>
                                            </Can>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </Col>
                </Row>
            </>
        );
    }
}

EmployeeRestrictionInfo.contextType = CommonContext;
